/**
 * FCM push notifications via @capacitor/push-notifications.
 * Native Android APK only — silently does nothing in browser.
 *
 * To swap Firebase project:
 *   1. Replace google-services.json in apps/web/android/app/
 *   2. Run `make android-sync` (or `pnpm cap sync android`)
 *   No backend or code changes needed — FIREBASE_* env vars on API handle the other side.
 */

import { isNativePlatform } from './platform'
import { api } from './api'

async function getPushPlugin() {
  if (!isNativePlatform()) return null
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications')
    return PushNotifications
  } catch {
    return null
  }
}

/**
 * Request permission, register for FCM, and save the token to the backend.
 * Call once after login.
 */
export async function initPushNotifications(): Promise<void> {
  const PushNotifications = await getPushPlugin()
  if (!PushNotifications) return

  const { receive: permStatus } = await PushNotifications.checkPermissions()

  let granted = permStatus === 'granted'
  if (!granted) {
    const result = await PushNotifications.requestPermissions()
    granted = result.receive === 'granted'
  }

  if (!granted) return

  await PushNotifications.register()

  // Receive the FCM token and save to backend
  await PushNotifications.addListener('registration', async ({ value: token }) => {
    try {
      await api.post('/users/me/fcm-token', { token })
    } catch (err) {
      console.warn('[FCM] Failed to save token:', err)
    }
  })

  // Log errors
  await PushNotifications.addListener('registrationError', (err) => {
    console.warn('[FCM] Registration error:', err)
  })

  // Handle push received while app is in foreground
  await PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('[FCM] Push received in foreground:', notification.title)
    // Foreground notifications are visible as local alerts by Capacitor automatically
  })

  // Handle tap on push notification — full reload to the target path
  // because we're outside React Router here
  await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const data = action.notification.data as { type?: string; rentalId?: string } | undefined
    if (data?.type === 'overdue' || data?.type === 'rental_opened' || data?.type === 'rental_closed') {
      window.location.href = '/manager/rentals'
    }
  })
}

/**
 * Remove FCM token from backend on logout.
 */
export async function clearPushToken(): Promise<void> {
  const PushNotifications = await getPushPlugin()
  if (!PushNotifications) return

  try {
    await api.delete('/users/me/fcm-token')
  } catch {
    // Non-critical
  }
}
